import { Link, useParams } from "react-router-dom";
import { api, useApi } from "../api";
import { PageShell } from "../components/Layout";
import { ChapterTag, FaqAccordion, Marquee } from "../components/Motion";
import Seo, { breadcrumbSchema, faqSchema } from "../components/Seo";
import { LinkPills, Loading, PageHero, SectionBlock } from "../components/Sections";

export default function CityService() {
  const { city, service } = useParams();
  const { data, loading, error } = useApi(() => api.combo(city, service), [city, service]);

  if (loading) {
    return (
      <PageShell>
        <Loading />
      </PageShell>
    );
  }
  if (error || !data) {
    return (
      <PageShell>
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4" data-testid="combo-not-found">
          <p className="font-display font-black uppercase text-4xl text-white">Page not found</p>
          <Link to="/locations" className="font-mono text-xs uppercase tracking-widest text-amber border border-amber px-5 py-3">
            View all UK locations
          </Link>
        </div>
      </PageShell>
    );
  }

  const origin = window.location.origin;
  const c = data.city;
  const s = data.service;
  const marqueeItems = [...s.keywords.map((k) => `${k.replace(/\b\w/g, (ch) => ch.toUpperCase())} ${c.name}`), ...c.areas.map((a) => `${s.name} ${a}`)];

  return (
    <PageShell>
      <Seo title={data.title} description={data.description} path={`/locations/${city}/${service}`} schema={faqSchema(data.faqs)} keywords={data.keywords} />
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema(data.breadcrumb, origin))}</script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: `${s.name} in ${c.name}`,
          provider: { "@type": "Organization", name: "Xtreme Protection" },
          areaServed: { "@type": "City", name: `${c.name}, ${c.county}` },
          description: data.description,
        })}
      </script>

      <PageHero eyebrow={data.eyebrow} lines={data.h1_lines} intro={data.intro} breadcrumb={data.breadcrumb} />
      <Marquee items={marqueeItems} />

      {data.sections.map((sec, i) => (
        <SectionBlock key={i} index={i} heading={sec.heading} paragraphs={sec.paragraphs} bullets={sec.bullets} />
      ))}

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="combo-faq-section">
        <ChapterTag num="FAQ" title={`${s.name.toUpperCase()} ${c.name.toUpperCase()} — QUESTIONS`} />
        <FaqAccordion faqs={data.faqs} testIdPrefix="combo-faq" />
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16 space-y-12">
        <div>
          <ChapterTag num="OPS" title={`OTHER SERVICES IN ${c.name.toUpperCase()}`} />
          <LinkPills
            items={data.other_services}
            prefix={`/locations/${city}`}
            labelFn={(o) => `${o.name} ${c.name}`}
            testIdPrefix="combo-other-service"
          />
        </div>
        <div>
          <ChapterTag num="GRID" title={`${s.name.toUpperCase()} IN NEARBY LOCATIONS`} />
          <LinkPills
            items={data.nearby}
            toFn={(n) => `/locations/${n.slug}/${service}`}
            labelFn={(n) => `${s.name} ${n.name}`}
            testIdPrefix="combo-nearby"
          />
        </div>
      </section>
    </PageShell>
  );
}
